import { useEffect } from 'react';

import { Icon, Spinner } from '@components/index';
import testId from '@constants/testId';
import { acceptedMemberAdditionFileFormats } from '@features/group-management/constants';
import useDragAndDrop from '@hooks/useDragAndDrop';
import { bytesToMB } from '@utils/file';

interface DragAndDropFileSectionProps {
  file?: File;
  isLoading: boolean;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  checkFileValidityAndProcess: (selectedFile: File) => void;
  onDeleteFile: () => void;
  fileUploadError: string;
  description?: string;
}

const DragAndDropFileSection = ({
  file,
  isLoading,
  onFileSelect,
  checkFileValidityAndProcess,
  onDeleteFile,
  fileUploadError,
  description = 'Maximum no. of rows allowed would be 100'
}: DragAndDropFileSectionProps) => {
  const {
    dragOver,
    setDragOver,
    onDragOver,
    onDragLeave,
    fileDropError,
    setFileDropError
  } = useDragAndDrop();

  const fileSizeInMB = bytesToMB(file?.size || 0);

  const getFileIconName = (): string => {
    if (isLoading) return 'svg-file';
    if (!file) return 'upload';
    return 'completed';
  };

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);

    const selectedFile = e?.dataTransfer?.files[0];
    checkFileValidityAndProcess(selectedFile);
  };

  const renderTitle = (): JSX.Element => {
    if (isLoading)
      return (
        <div className="flex items-center justify-center">
          Uploading Document...
          <Spinner className="mx-4 size-6" />
        </div>
      );
    if (!file)
      return (
        <>
          Drag and drop file here or{' '}
          <label
            htmlFor="file-input"
            className="cursor-pointer text-primary underline"
            data-testid={testId.fileUpload.uploadText}
          >
            Upload a CSV or Excel File
          </label>
        </>
      );
    return <>Uploaded successfully!</>;
  };

  useEffect(() => {
    setFileDropError(fileUploadError);
  }, [fileUploadError, setFileDropError]);

  return (
    <>
      {!file && !isLoading && (
        <input
          type="file"
          id="file-input"
          className="hidden"
          onChange={onFileSelect}
          accept={acceptedMemberAdditionFileFormats}
        />
      )}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`mt-3 flex min-h-60 flex-col items-center justify-center rounded-md border border-dashed border-cofeeGreyBlue200 bg-cofeeNeutral1 ${
          dragOver ? 'bg-grey70' : ''
        } ${isLoading ? 'bg-primary10' : ''}`}
      >
        {fileDropError && (
          <div className="w-full rounded-t-md bg-errorLite py-0.5 text-center text-xs text-error">
            {fileDropError}
          </div>
        )}
        <div
          className={`flex w-full flex-col items-center justify-center px-6 ${
            fileDropError ? 'pb-6 pt-3' : 'py-8'
          }`}
        >
          <Icon name={getFileIconName()} size="none" className="size-18" />

          <div
            className={`mt-4 text-sm font-bold ${
              !file ? 'text-grey' : 'text-black'
            }`}
          >
            {renderTitle()}
          </div>
          {!file ? (
            <div className="mt-3 text-sm text-grey">{description}</div>
          ) : (
            <div className="mt-3 flex items-center text-sm text-grey">
              <div>{file?.name}</div>
              <Icon name="greyDot" size="small" className="mx-1 !min-w-2.5" />
              <div className="mr-2">{fileSizeInMB}</div>
              <div
                onClick={onDeleteFile}
                role="presentation"
                className="cursor-pointer border-l-2 px-2 text-error40"
              >
                Cancel
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DragAndDropFileSection;
